/**
 * AYRAA — home page behaviours.
 *
 * Two small, additive pieces. Nothing here patches a theme component or an
 * app section; the shoppable video slider, the hotspot carousel and the
 * theme's own scroll animations all run exactly as they did before.
 *
 *   1. <ay-happy-customers>  A centre-mode carousel over the customer rail:
 *      arrows, dots, autoplay, a seamless loop and a full-size view. The rail
 *      itself scrolls natively with snap points, so swipe, trackpad,
 *      shift-wheel and keyboard all work with no JS at all; everything here
 *      is layered on top of that rather than replacing it.
 *
 *   2. Judge.me guard      If the Judge.me script has not filled the reviews
 *      carousel — no reviews yet, widget disabled, script blocked — the
 *      section collapses to its heading instead of leaving an empty band.
 */

(function () {
  'use strict';

  var REDUCED = window.matchMedia('(prefers-reduced-motion: reduce)');

  /* ======================================================================
     1. Happy customers rail

     The rail scrolls natively with snap points, so swipe, trackpad,
     shift-wheel and keyboard all work with no JavaScript at all. Everything
     below is layered on top of that: the arrows, the dots, the tap-to-enlarge
     dialog, which card is treated as the centre one, and the autoplay.

     Centre mode needs the first and last cards to be able to reach the middle
     of the rail, which they cannot do against its own edges. The padding that
     buys them that room is set here rather than in CSS because every card is
     a different width -- each one is its image's own shape -- so the figure is
     only knowable once the images have laid out.

     The loop works on a second, identical set of cards drawn by Liquid. When
     the scroll passes the width of one set, the position drops back by exactly
     that width. The pixels either side of the jump are identical, so it is
     invisible, and the carousel runs on rather than rewinding. The jump is
     made with snapping and smooth scrolling switched off for the instant it
     takes, because either one would animate it and give the trick away.
     ====================================================================== */
  class AYHappyCustomers extends HTMLElement {
    connectedCallback() {
      this.section = this.closest('.ay-hc');
      if (!this.section) return;

      this.cards = Array.from(this.querySelectorAll('.ay-hc__card'));
      if (!this.cards.length) return;

      this.realCount = parseInt(this.dataset.count, 10) || this.cards.length;
      this.centreMode = this.dataset.centre === 'true';
      this.wantsAutoplay = this.dataset.autoplay === 'true';
      this.delay = (parseFloat(this.dataset.delay) || 3) * 1000;
      this.setWidth = 0;
      this.paused = false;

      this.prev = this.section.querySelector('.js-ay-hc-prev');
      this.next = this.section.querySelector('.js-ay-hc-next');
      this.dots = Array.from(this.section.querySelectorAll('.js-ay-hc-dot'));
      this.dialog = this.section.querySelector('.js-ay-hc-dialog');
      this.dialogImg = this.section.querySelector('.js-ay-hc-dialog-img');

      this.onScroll = this.throttle(this.sync.bind(this));
      this.onResize = this.throttle(this.measure.bind(this));
      this.settle = null;
      this.resume = null;

      if (this.prev) this.prev.addEventListener('click', this.step.bind(this, -1));
      if (this.next) this.next.addEventListener('click', this.step.bind(this, 1));
      this.addEventListener('scroll', this.onScroll, { passive: true });
      window.addEventListener('resize', this.onResize, { passive: true });

      this.bindDots();
      this.bindPausing();
      if (this.dialog) this.bindDialog();
      this.bindVideos();

      this.measure();
      this.startAutoplay();

      /* Images are lazy loaded and each card's width comes from its image, so
         the geometry this all rests on is not final at connect time. */
      if (window.ResizeObserver) {
        this.observer = new ResizeObserver(this.onResize);
        this.observer.observe(this);
      }
    }

    disconnectedCallback() {
      this.stopAutoplay();
      window.removeEventListener('resize', this.onResize);
      if (this.observer) this.observer.disconnect();
      if (this.resume) clearTimeout(this.resume);
      if (this.settle) clearTimeout(this.settle);
    }

    /* ---------------------------------------------------------------- *
     * Geometry
     * ---------------------------------------------------------------- */
    measure() {
      this.applyCentrePadding();

      /* One set's width is the distance between a card and its own copy.
         Measured, not summed, so the gaps are included whatever they are. */
      this.setWidth = this.cards.length > this.realCount
        ? this.cards[this.realCount].offsetLeft - this.cards[0].offsetLeft
        : 0;

      this.sync();
    }

    applyCentrePadding() {
      if (!this.centreMode) {
        this.style.paddingLeft = '';
        this.style.paddingRight = '';
        return;
      }
      var first = this.cards[0];
      var last = this.cards[this.cards.length - 1];
      /* With this padding, "card one is centred" lands on scrollLeft 0, which
         is what lets the loop jump by a whole set and stay aligned. */
      this.style.paddingLeft = Math.max(0, (this.clientWidth - first.offsetWidth) / 2) + 'px';
      this.style.paddingRight = Math.max(0, (this.clientWidth - last.offsetWidth) / 2) + 'px';
    }

    leftPad() {
      return parseFloat(getComputedStyle(this).paddingLeft) || 0;
    }

    /* ---------------------------------------------------------------- *
     * Which card is the middle one
     * ---------------------------------------------------------------- */
    /** The middle of the rail, in screen coordinates. */
    railMid() {
      var box = this.getBoundingClientRect();
      return box.left + box.width / 2;
    }

    /** A card's middle, in the same space. The rect is the scaled one, but a
     *  scale about the centre leaves the centre where it was. */
    cardMid(card) {
      var box = card.getBoundingClientRect();
      return box.left + box.width / 2;
    }

    centreCard() {
      var mid = this.railMid();
      var best = this.cards[0];
      var bestDistance = Infinity;
      for (var i = 0; i < this.cards.length; i++) {
        var distance = Math.abs(this.cardMid(this.cards[i]) - mid);
        if (distance < bestDistance) {
          bestDistance = distance;
          best = this.cards[i];
        }
      }
      return best;
    }

    sync() {
      var centre = this.centreCard();
      for (var i = 0; i < this.cards.length; i++) {
        this.cards[i].classList.toggle('is-centre', this.cards[i] === centre);
      }

      var index = Number(centre.dataset.index) || 0;
      for (var d = 0; d < this.dots.length; d++) {
        this.dots[d].classList.toggle('is-on', d === index);
        this.dots[d].setAttribute('aria-current', d === index ? 'true' : 'false');
      }

      this.syncArrows();
      this.scheduleWrap();
    }

    syncArrows() {
      if (!this.prev || !this.next) return;
      var nav = this.section.querySelector('.ay-hc__nav');
      var max = this.scrollWidth - this.clientWidth;

      if (this.setWidth) {
        // A loop has no ends to be stuck against.
        this.prev.disabled = false;
        this.next.disabled = false;
      } else {
        this.prev.disabled = this.scrollLeft <= this.leftPad() + 2;
        this.next.disabled = this.scrollLeft >= max - 2;
      }
      // Nothing to scroll: hide the pair rather than show two dead buttons.
      if (nav) nav.hidden = max <= 2;
    }

    /* ---------------------------------------------------------------- *
     * The loop
     * ---------------------------------------------------------------- */
    scheduleWrap() {
      if (!this.setWidth) return;
      if (this.settle) clearTimeout(this.settle);
      /* Only once the scroll has come to rest. Shifting the position mid
         animation cancels it, which reads as the carousel stumbling. */
      this.settle = setTimeout(this.wrap.bind(this), 140);
    }

    wrap() {
      if (!this.setWidth) return;
      if (this.scrollLeft >= this.setWidth) this.shiftBy(-this.setWidth);
    }

    shiftBy(delta) {
      var behavior = this.style.scrollBehavior;
      var snap = this.style.scrollSnapType;
      this.style.scrollBehavior = 'auto';
      this.style.scrollSnapType = 'none';
      this.scrollLeft = this.scrollLeft + delta;
      void this.offsetWidth; // settle the new position before snapping returns
      this.style.scrollSnapType = snap;
      this.style.scrollBehavior = behavior;
    }

    /* ---------------------------------------------------------------- *
     * Moving
     * ---------------------------------------------------------------- */
    goTo(card) {
      if (!card) return;
      /* Worked out as a distance to travel rather than a position to land on,
         so it needs no shared origin between the card and the scroll box. Mid
         animation both readings move together, so the sum still points at the
         same resting place. */
      var delta = this.centreMode
        ? this.cardMid(card) - this.railMid()
        : card.getBoundingClientRect().left - (this.getBoundingClientRect().left + this.leftPad());
      this.scrollTo({
        left: this.scrollLeft + delta,
        behavior: REDUCED.matches ? 'auto' : 'smooth'
      });
    }

    step(direction) {
      var index = this.cards.indexOf(this.centreCard());
      var target = this.cards[index + direction];

      if (!target) {
        if (!this.setWidth) {
          // No loop, so the ends are ends: go round rather than sit still.
          target = direction > 0 ? this.cards[0] : this.cards[this.cards.length - 1];
        } else if (direction < 0) {
          // Off the front of the first set: hop forward a set and step back.
          this.shiftBy(this.setWidth);
          target = this.cards[this.cards.indexOf(this.centreCard()) - 1];
        } else {
          this.wrap();
          target = this.cards[this.cards.indexOf(this.centreCard()) + 1];
        }
      }
      this.goTo(target);
    }

    /* ---------------------------------------------------------------- *
     * Autoplay
     * ---------------------------------------------------------------- */
    startAutoplay() {
      if (!this.wantsAutoplay || REDUCED.matches) return;
      if (this.cards.length < 2) return;
      this.stopAutoplay();
      this.timer = setInterval(this.tick.bind(this), this.delay);
    }

    stopAutoplay() {
      if (this.timer) clearInterval(this.timer);
      this.timer = null;
    }

    tick() {
      if (this.paused) return;
      if (document.hidden) return;
      if (this.dialog && this.dialog.open) return;
      // Wrap before moving, so a jump never lands inside an animation.
      if (this.setWidth && this.scrollLeft >= this.setWidth - 1) this.shiftBy(-this.setWidth);
      this.step(1);
    }

    bindPausing() {
      var self = this;
      var hold = function () {
        self.paused = true;
        if (self.resume) clearTimeout(self.resume);
      };
      var release = function (afterMs) {
        if (self.resume) clearTimeout(self.resume);
        self.resume = setTimeout(function () { self.paused = false; }, afterMs || 0);
      };

      /* Hover only counts where hovering is a real thing. On a touch screen a
         tap fires pointerenter with no pointerleave to follow, which would
         park the carousel for good. */
      if (window.matchMedia('(hover: hover)').matches) {
        this.section.addEventListener('pointerenter', hold);
        this.section.addEventListener('pointerleave', function () { release(0); });
      }
      this.section.addEventListener('focusin', hold);
      this.section.addEventListener('focusout', function () { release(0); });

      // A swipe: give them a moment to look before it starts moving again.
      this.addEventListener('pointerdown', hold, { passive: true });
      this.addEventListener('pointerup', function () { release(2000); }, { passive: true });
      this.addEventListener('pointercancel', function () { release(2000); }, { passive: true });
    }

    bindDots() {
      var self = this;
      this.dots.forEach(function (dot) {
        dot.addEventListener('click', function () {
          var wanted = Number(dot.dataset.index);
          /* The nearest card carrying that index, so a dot press moves as
             little as possible instead of rewinding through the whole set. */
          var mid = self.railMid();
          var best = null;
          var bestDistance = Infinity;
          self.cards.forEach(function (card) {
            if (Number(card.dataset.index) !== wanted) return;
            var distance = Math.abs(self.cardMid(card) - mid);
            if (distance < bestDistance) {
              bestDistance = distance;
              best = card;
            }
          });
          self.goTo(best);
        });
      });
    }

    bindDialog() {
      var self = this;

      this.querySelectorAll('.js-ay-hc-zoom').forEach(function (button) {
        button.addEventListener('click', function () {
          self.dialogImg.src = button.dataset.ayHcFull;
          self.dialogImg.alt = button.dataset.ayHcAlt || '';
          self.dialog.showModal();
        });
      });

      var close = this.section.querySelector('.js-ay-hc-close');
      if (close) close.addEventListener('click', function () { self.dialog.close(); });

      // Click the backdrop to dismiss. The dialog itself fills its own box,
      // so anything outside that box is the backdrop.
      this.dialog.addEventListener('click', function (event) {
        var box = self.dialog.getBoundingClientRect();
        var outside =
          event.clientY < box.top ||
          event.clientY > box.bottom ||
          event.clientX < box.left ||
          event.clientX > box.right;
        if (outside) self.dialog.close();
      });

      // Release the source once it is shut so a big file is not held open.
      this.dialog.addEventListener('close', function () {
        self.dialogImg.removeAttribute('src');
      });
    }

    // Video cards carry preload="none" and no autoplay, so nothing is fetched
    // until a clip is actually on screen; it plays there and pauses once it
    // leaves -- the viewport is the root, and the rail's own overflow clips
    // the cards scrolled out sideways. Reduced motion keeps the poster frame.
    bindVideos() {
      var videos = this.querySelectorAll('.js-ay-hc-video');
      if (!videos.length || REDUCED.matches || !('IntersectionObserver' in window)) return;

      var observer = new IntersectionObserver(function (entries) {
        entries.forEach(function (entry) {
          var video = entry.target;
          if (entry.isIntersecting) {
            var playing = video.play();
            if (playing) playing.catch(function () {});
          } else {
            video.pause();
          }
        });
      }, { rootMargin: '200px 0px' });

      videos.forEach(function (video) { observer.observe(video); });
    }

    throttle(fn) {
      var frame = null;
      return function () {
        if (frame) return;
        frame = requestAnimationFrame(function () {
          frame = null;
          fn();
        });
      };
    }
  }

  if (!customElements.get('ay-happy-customers')) {
    customElements.define('ay-happy-customers', AYHappyCustomers);
  }

  /* ======================================================================
     2. Judge.me guard
     ====================================================================== */
  function watchReviewWidget() {
    var widget = document.querySelector('.js-ay-rv-widget');
    if (!widget) return;

    var section = widget.closest('.ay-rv');
    var settled = false;

    function hasContent() {
      // Judge.me writes review items into the container it was given. An
      // app block that rendered its own markup counts too.
      return (
        widget.querySelector('.jdgm-carousel-item, .jdgm-rev, .jdgm-widget__content') !== null ||
        widget.querySelector('.shopify-block') !== null
      );
    }

    function settle() {
      if (settled) return;
      if (hasContent()) {
        settled = true;
        observer.disconnect();
        return;
      }
      section.classList.add('ay-rv--empty');
    }

    var observer = new MutationObserver(function () {
      if (hasContent()) {
        settled = true;
        section.classList.remove('ay-rv--empty');
        observer.disconnect();
      }
    });

    observer.observe(widget, { childList: true, subtree: true });

    // Judge.me loads its script asynchronously; give it a clear run before
    // deciding the widget is not coming.
    setTimeout(settle, 5000);
    setTimeout(function () { observer.disconnect(); }, 15000);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', watchReviewWidget);
  } else {
    watchReviewWidget();
  }
})();
